import React, { useState } from 'react';
import {
  Bell,
  X,
  Eye,
  AlertTriangle,
  Info,
  CheckCircle,
  Settings,
  Package,
  Wrench,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  ChevronUp,
  MoreHorizontal
} from 'lucide-react';
import { useAnnouncements } from '../hooks/useAnnouncements';
import { useNotifications } from '../hooks/useNotifications';
import { sanitizeHtml, sanitizeHtmlPreview } from '../utils/sanitizeHtml';

interface AnnouncementNotificationsProps {
  className?: string;
}

const AnnouncementNotifications: React.FC<AnnouncementNotificationsProps> = ({ className = '' }) => {
  const { announcements, loading, markAsRead, markAsDismissed } = useAnnouncements();
  const { showNotification } = useNotifications();
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedAnnouncement, setSelectedAnnouncement] = useState<any>(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  
  const itemsPerPage = 5;

  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'info':
        return Info;
      case 'success':
        return CheckCircle;
      case 'warning':
        return AlertTriangle;
      case 'danger':
        return AlertTriangle;
      case 'feature':
        return Package;
      case 'maintenance':
        return Settings;
      default:
        return Bell;
    }
  };

  const getTypeColor = (type: string) => {
    switch (type) {
      case 'info':
        return 'border-primary/30 bg-primary/10 dark:bg-primary/20 dark:border-primary-dark';
      case 'success':
        return 'border-green-200 bg-green-50 dark:bg-green-900/20 dark:border-green-800';
      case 'warning':
        return 'border-yellow-200 bg-yellow-50 dark:bg-yellow-900/20 dark:border-yellow-800';
      case 'danger':
        return 'border-red-200 bg-red-50 dark:bg-red-900/20 dark:border-red-800';
      case 'feature':
        return 'border-purple-200 bg-purple-50 dark:bg-purple-900/20 dark:border-purple-800';
      case 'maintenance':
        return 'border-gray-200 bg-gray-50 dark:bg-gray-900/20 dark:border-gray-800';
      default:
        return 'border-primary/30 bg-primary/10 dark:bg-primary/20 dark:border-primary-dark';
    }
  };

  const getIconColor = (type: string) => {
    switch (type) {
      case 'info':
        return 'text-primary dark:text-primary';
      case 'success':
        return 'text-green-600 dark:text-green-400';
      case 'warning':
        return 'text-yellow-600 dark:text-yellow-400';
      case 'danger':
        return 'text-red-600 dark:text-red-400';
      case 'feature':
        return 'text-purple-600 dark:text-purple-400';
      case 'maintenance':
        return 'text-gray-600 dark:text-gray-400';
      default:
        return 'text-primary dark:text-primary';
    }
  };

  const handleMarkAsRead = async (announcementId: number) => {
    try {
      await markAsRead(announcementId);
      showNotification('Duyuru okundu olarak işaretlendi', 'success');
    } catch (error) {
      showNotification('Duyuru işaretlenirken hata oluştu', 'error');
    }
  };

  const handleDismiss = async (announcementId: number) => {
    try {
      await markAsDismissed(announcementId);
      showNotification('Duyuru kapatıldı', 'success');
    } catch (error) {
      showNotification('Duyuru kapatılırken hata oluştu', 'error');
    }
  };

  const showDetail = (announcement: any) => {
    setSelectedAnnouncement(announcement);
    setShowDetailModal(true);
    if (!announcement.is_read) {
      handleMarkAsRead(announcement.id);
    }
  };

  // Pagination logic
  const totalPages = Math.ceil(announcements.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentAnnouncements = announcements.slice(startIndex, endIndex);

  if (loading) {
    return (
      <div className={`space-y-4 ${className}`}>
        {[...Array(2)].map((_, i) => (
          <div key={i} className="animate-pulse">
            <div className="bg-gray-200 dark:bg-gray-700 h-20 rounded-lg"></div>
          </div>
        ))}
      </div>
    );
  }

  if (announcements.length === 0) {
    return (
      <div className={`text-center py-6 text-gray-500 dark:text-gray-400 ${className}`}>
        <div className="bg-primary/10 dark:bg-primary/20 p-4 rounded-lg flex items-center justify-center space-x-3 border border-primary/20 dark:border-primary-dark">
          <CheckCircle className="h-6 w-6 text-primary dark:text-primary" />
          <p className="font-medium text-primary-dark dark:text-accent">Yeni Duyuru Yok</p>
        </div>
      </div>
    );
  }

  // En son duyuru (en yüksek ID'li)
  const latestAnnouncement = announcements.length > 0 ? announcements[0] : null;
  const otherAnnouncements = announcements.slice(1);

  return (
    <div className={`space-y-4 ${className}`}>
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center">
          <Bell className="h-5 w-5 mr-2 text-primary" />
          Yeni Duyurular ({announcements.length})
        </h3>
        {announcements.length > 1 && (
          <button
            onClick={() => setIsExpanded(!isExpanded)}
            className="flex items-center space-x-1 text-sm text-primary hover:text-primary-dark dark:text-primary dark:hover:text-accent transition-colors"
          >
            <span>{isExpanded ? 'Gizle' : 'Tümünü Gör'}</span>
            {isExpanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
          </button>
        )}
      </div>

      {/* En son duyuru - her zaman göster */}
      {latestAnnouncement && (
        <div className="mb-4">
          {(() => {
            const IconComponent = getTypeIcon(latestAnnouncement.type);
            const typeColor = getTypeColor(latestAnnouncement.type);
            const iconColor = getIconColor(latestAnnouncement.type);

            return (
              <div
                className={`border-l-4 border rounded-lg p-4 shadow-sm ${typeColor} relative`}
              >
                <div className="flex items-start justify-between">
                  <div className="flex items-start space-x-3 flex-1">
                    <div className={`p-2 rounded-full bg-white dark:bg-gray-800 ${iconColor}`}>
                      <IconComponent className="h-4 w-4" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center space-x-2 mb-2">
                        <h4 className="text-sm font-semibold text-gray-900 dark:text-white truncate">
                          {latestAnnouncement.title}
                        </h4>
                        {latestAnnouncement.priority > 3 && (
                          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200">
                            Yüksek Öncelik
                          </span>
                        )}
                      </div>
                      <div 
                        className="text-sm text-gray-700 dark:text-gray-300 mb-2"
                        dangerouslySetInnerHTML={{ 
                          __html: sanitizeHtmlPreview(latestAnnouncement.content, 150) 
                        }}
                      />
                      <div className="flex items-center justify-between">
                        <p className="text-xs text-gray-500 dark:text-gray-400">
                          {latestAnnouncement.created_by_name} • {new Date(latestAnnouncement.created_at).toLocaleDateString('tr-TR')}
                        </p>
                        <div className="flex items-center space-x-2">
                          <button
                            onClick={() => showDetail(latestAnnouncement)}
                            className="text-gray-400 hover:text-primary transition-colors"
                            title="Detayları Görüntüle"
                          >
                            <Eye className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => handleDismiss(latestAnnouncement.id)}
                            className="text-gray-400 hover:text-red-600 transition-colors"
                            title="Kapat"
                          >
                            <X className="h-4 w-4" />
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                {!latestAnnouncement.is_read && (
                  <div className="absolute top-2 right-2 w-2 h-2 bg-primary rounded-full"></div>
                )}
              </div>
            );
          })()}
        </div>
      )}

      {/* Diğer duyurular - sadece genişletilmiş durumda göster */}
      {isExpanded && otherAnnouncements.length > 0 && (
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 flex items-center">
              <MoreHorizontal className="h-4 w-4 mr-1" />
              Diğer Duyurular ({otherAnnouncements.length})
            </h4>
          </div>
          
          {otherAnnouncements.map((announcement) => {
            const IconComponent = getTypeIcon(announcement.type);
            const typeColor = getTypeColor(announcement.type);
            const iconColor = getIconColor(announcement.type);

            return (
              <div
                key={announcement.id}
                className={`border-l-4 border rounded-lg p-3 shadow-sm ${typeColor} relative`}
              >
                <div className="flex items-start justify-between">
                  <div className="flex items-start space-x-3 flex-1">
                    <div className={`p-1.5 rounded-full bg-white dark:bg-gray-800 ${iconColor}`}>
                      <IconComponent className="h-3.5 w-3.5" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center space-x-2 mb-1">
                        <h4 className="text-sm font-medium text-gray-900 dark:text-white truncate">
                          {announcement.title}
                        </h4>
                        {announcement.priority > 3 && (
                          <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200">
                            Yüksek
                          </span>
                        )}
                      </div>
                      <div 
                        className="text-xs text-gray-600 dark:text-gray-400 mb-2"
                        dangerouslySetInnerHTML={{ 
                          __html: sanitizeHtmlPreview(announcement.content, 100) 
                        }}
                      />
                      <div className="flex items-center justify-between">
                        <p className="text-xs text-gray-500 dark:text-gray-400">
                          {new Date(announcement.created_at).toLocaleDateString('tr-TR')}
                        </p>
                        <div className="flex items-center space-x-1">
                          <button
                            onClick={() => showDetail(announcement)}
                            className="text-gray-400 hover:text-primary transition-colors"
                            title="Detayları Görüntüle"
                          >
                            <Eye className="h-3.5 w-3.5" />
                          </button>
                          <button
                            onClick={() => handleDismiss(announcement.id)}
                            className="text-gray-400 hover:text-red-600 transition-colors"
                            title="Kapat"
                          >
                            <X className="h-3.5 w-3.5" />
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                {!announcement.is_read && (
                  <div className="absolute top-2 right-2 w-1.5 h-1.5 bg-primary rounded-full"></div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Detail Modal */}
      {showDetailModal && selectedAnnouncement && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[70] p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200 dark:border-gray-700">
              <div className="flex justify-between items-start">
                <div className="flex items-center space-x-3">
                  <div className={`p-3 rounded-full ${getIconColor(selectedAnnouncement.type)} bg-gray-100 dark:bg-gray-700`}>
                    {React.createElement(getTypeIcon(selectedAnnouncement.type), { className: "h-6 w-6" })}
                  </div>
                  <div>
                    <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                      {selectedAnnouncement.title}
                    </h2>
                    <div className="flex items-center space-x-3 mt-1">
                      <p className="text-sm text-gray-500 dark:text-gray-400">
                        {selectedAnnouncement.created_by_name}
                      </p>
                      <p className="text-sm text-gray-500 dark:text-gray-400">
                        {new Date(selectedAnnouncement.created_at).toLocaleString('tr-TR')}
                      </p>
                      {selectedAnnouncement.priority > 3 && (
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200">
                          Yüksek Öncelik
                        </span>
                      )}
                    </div>
                  </div>
                </div>
                <button
                  onClick={() => setShowDetailModal(false)}
                  className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
                >
                  <X className="h-6 w-6" />
                </button>
              </div>
            </div>
            
            <div className="p-6">
              <div
                className="prose dark:prose-invert max-w-none text-gray-700 dark:text-gray-300 prose-img:rounded-lg prose-img:shadow-md prose-video:rounded-lg prose-video:shadow-md prose-a:text-primary dark:prose-a:text-primary prose-blockquote:border-l-blue-500 prose-code:bg-gray-100 dark:prose-code:bg-gray-800 prose-code:px-1 prose-code:py-0.5 prose-code:rounded"
                dangerouslySetInnerHTML={{ __html: sanitizeHtml(selectedAnnouncement.content) }}
              />
            </div>

            <div className="p-6 border-t border-gray-200 dark:border-gray-700 flex justify-end space-x-3">
              <button
                onClick={() => setShowDetailModal(false)}
                className="px-4 py-2 text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
              >
                Kapat
              </button>
              <button
                onClick={() => {
                  handleDismiss(selectedAnnouncement.id);
                  setShowDetailModal(false);
                }}
                className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors"
              >
                Duyuruyu Kapat
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AnnouncementNotifications;